/**
 * The default `TaskActions` any page can hand a `TaskList`/`TaskDrawer`: complete (with the
 * §8.4 celebration), undo, defer, star, edit, drop, delete — each just the direct API call.
 *
 * Extracted so a new page gains all seven at once instead of reimplementing a subset by
 * hand. That reimplementation is exactly how the Areas & projects page ended up with no
 * `onUndo` at all: `TaskDrawer`'s own undo wrapper calls `actions.onUndo?.(task)`, which
 * silently does nothing when the field is missing, so a completed-then-undone task in a
 * drawer looked like it came back but stayed `done` server-side (DECISIONS.md D101).
 *
 * Briefing.tsx does not use this — its own `complete`/`undo` hold a ticked task in place
 * across its page-level open/starred lists (`justDone`), which is a page-specific concern
 * this helper has no opinion about. `TaskDrawer` already holds its own place independently
 * (`justDone.ts`) regardless of which page mounts it, so this alone is everything a
 * drawer-only context needs.
 *
 * **Offline (§8.2, D157).** Complete, defer, edit and breakdown queue via `taskQueue.ts` on
 * `ApiError.isOffline`, the same scope `useHeldTaskActions.ts` covers — everything else here
 * (undo, star, drop, delete, next-action, reorder) stays online-only.
 */

import { ApiError, api } from "./api";
import type { TaskActions } from "./components/TaskList";
import { celebrate, type BurstFn } from "./components/fireworksContext";
import { postNotice } from "./notices";
import { queueTaskComplete, queueTaskDefer, queueTaskSubtasks } from "./taskQueue";

export function basicTaskActions(burst: BurstFn): TaskActions {
  return {
    onComplete: async (task, at) => {
      // §8.4: completion is a celebration, once per point of activation cost — the same
      // burst every other task row gets, fired before the request so it happens where the
      // row was.
      if (at) celebrate(burst, at.x, at.y, task.energy);
      try {
        await api.completeTask(task.id);
      } catch (err) {
        if (!(err instanceof ApiError && err.isOffline)) throw err;
        await queueTaskComplete(task.id);
        postNotice(`"${task.title}" is queued - it'll complete once you're back online.`);
      }
    },
    onUndo: async (task) => {
      await api.patchTask(task.id, { status: "open" });
    },
    onDefer: async (task) => {
      try {
        await api.deferTask(task.id);
      } catch (err) {
        if (!(err instanceof ApiError && err.isOffline)) throw err;
        await queueTaskDefer(task.id);
        postNotice(`"${task.title}" is queued to snooze - it'll apply once you're back online.`);
      }
    },
    onStar: async (task) => {
      await api.patchTask(task.id, { starred: !task.starred });
    },
    // Queues itself when offline (`queueable` in `api.ts`), like every other plain field
    // write in the app — no branch needed here (D168).
    onEdit: async (task, patch) => {
      await api.patchTask(task.id, patch);
    },
    onDrop: async (task) => {
      await api.patchTask(task.id, { status: "dropped" });
    },
    onDelete: async (task) => {
      await api.deleteTask(task.id);
    },
    onBreakdown: async (task, titles) => {
      // The server dispatches create vs. split by whether `task.id` already has a parent
      // (§8.6.2, §10) — one call serves both, and so does the offline queue (taskQueue.ts).
      try {
        return (await api.createSubtasks(task.id, titles)).subtasks;
      } catch (err) {
        if (!(err instanceof ApiError && err.isOffline)) throw err;
        const queued = await queueTaskSubtasks(task, titles);
        postNotice(`Queued - these steps will be added once you're back online.`);
        return queued.kind === "subtasks"
          ? queued.ids.map((id, i) => ({ ...task, id, title: queued.titles[i], parent_id: task.id }))
          : undefined;
      }
    },
    // Online-only, unlike `onBreakdown` above — there's no queueable shape for "ask a model
    // something" the way there is for a titles list already typed out (§8.2, D157). Errors
    // (including offline) propagate to `AiBreakdownButton`, which is what shows them.
    onAiBreakdown: async (task, tier) => {
      return (await api.aiBreakdown(task.id, tier)).subtasks;
    },
    onUndoBreakdown: async (parent, undo) => {
      await api.undoBreakdown(parent.id, {
        created: undo.created,
        restore_id: undo.restore?.id,
        title: undo.restore?.title,
        sort_order: undo.restore?.sortOrder,
      });
    },
    onSetNextAction: async (parent, subtaskId) => {
      await api.setNextAction(parent.id, subtaskId);
    },
    onReorderSubtasks: async (parent, ids) => {
      await api.reorderSubtasks(parent.id, ids);
    },
  };
}
